import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { PaginationInfo } from "../../../../../types/general-types";
import { intIdToUuid, uuidToIntId } from "../../../../../helpers/uuid";
import apiClient from "../../../../../helpers/api-client";

export const ALL_SESSION_TYPES = ["active", "upcoming", "completed"] as const;
export type SessionType = (typeof ALL_SESSION_TYPES)[number];

export type AssessmentResponse = Awaited<ReturnType<typeof apiClient.assessment.getAssessments>>[0];

export type Assessment = {
	id: number;
	uuid: string;
	name?: string;
};

export type LiveSessionUser = {
	userId: string;
	role: "proctor" | "student";
};

export type LiveSession = {
	id: string;
	courseId: string;
	assessmentId: string;
	name: string;
	startTime: string;
	endTime: string;
	type: SessionType;
	users: LiveSessionUser[];
};

type LiveUserSession = {
	role: string;
};

export type LiveSessionActiveState = {
	userSessions: LiveUserSession[];
};

export type BaseLiveSessionQuery = {
	instituteUuid: string;
	courseUuid: string;
	type: SessionType;
};

function createBaseLiveSessionSearchParams({ instituteUuid, courseUuid, type }: BaseLiveSessionQuery) {
	const searchParams = new URLSearchParams();
	if (type === "completed") {
		searchParams.set("endingBefore", new Date().toISOString());
	} else {
		searchParams.set("type", type);
	}
	searchParams.set("instituteId", instituteUuid);
	searchParams.set("courseId", courseUuid);
	searchParams.set("includeUsers", "true");
	return searchParams;
}

function transformAssessment(assessment: AssessmentResponse) {
	return {
		...assessment,
		uuid: intIdToUuid(assessment.id),
		name: typeof assessment.quizInfo === "object" ? (assessment.quizInfo?.name ?? undefined) : undefined,
	};
}

const liveSessionsApi = createApi({
	reducerPath: "liveSessions",
	baseQuery: async (arg, api, extraOptions) =>
		fetchBaseQuery({
			fetchFn: apiClient.fetch.bind(apiClient),
			baseUrl: `/`,
			prepareHeaders: (headers) => {
				headers.set("Content-Type", "application/json");
				return headers;
			},
		})(arg, api, extraOptions),
	tagTypes: [
		"LiveSessionCount",
		"LiveSessions",
		"LiveSession",
		"AssessmentNameSuffix",
		"Assessment",
		"LiveSessionActiveState",
	],
	endpoints: (builder) => ({
		getLiveSessionCount: builder.query<number, BaseLiveSessionQuery>({
			query: (query) => {
				const searchParams = createBaseLiveSessionSearchParams(query);
				searchParams.set("size", "1");
				searchParams.set("page", "0");
				return `api/live/liveSession?${searchParams.toString()}`;
			},
			transformResponse: ({ totalElements }: PaginationInfo) => totalElements,
			providesTags: (_result, _error, { type }) => [{ type: "LiveSessionCount", id: type }],
		}),
		getLiveSessions: builder.query<LiveSession[], BaseLiveSessionQuery>({
			query: (query) => {
				const searchParams = createBaseLiveSessionSearchParams(query);
				searchParams.set("sortBy", "startTime");
				return `api/live/liveSession?${searchParams.toString()}`;
			},
			onCacheEntryAdded: async (_arg, { cacheDataLoaded, dispatch }) => {
				const data = await cacheDataLoaded;
				const assessmentIds = new Set(data.data.map(({ assessmentId }) => uuidToIntId(assessmentId)));
				await Promise.allSettled(
					[...assessmentIds].map((assessmentId) => {
						if (!liveSessionsApi.endpoints.getAssessment.select(assessmentId)) {
							return dispatch(liveSessionsApi.endpoints.getAssessment.initiate(assessmentId));
						}
					}),
				);
				if (!liveSessionsApi.endpoints.getAssessmentNameSuffix.select()) {
					await dispatch(liveSessionsApi.endpoints.getAssessmentNameSuffix.initiate());
				}
			},
			transformResponse: (
				{
					results,
				}: {
					results: Omit<LiveSession, "type">[];
				},
				_meta,
				{ type },
			) => results.map((session) => ({ ...session, type })),
			providesTags: (result, _, { type }) => [
				{
					type: "LiveSessions",
					id: type,
				},
				...(result
					? result.map((session) => ({
							type: "LiveSession" as const,
							id: session.id,
						}))
					: []),
			],
		}),
		getAssessmentNameSuffix: builder.query<string | undefined, void>({
			query: () => `/api/verity/institute-settings/suffix`,
			transformResponse: ({ suffix }: { suffix?: string }) => suffix,
			providesTags: () => ["AssessmentNameSuffix"],
		}),
		getAssessment: builder.query<Assessment, number>({
			query: (id) => `/api/verity/assessment/${id}`,
			transformResponse: transformAssessment,
			providesTags: (_result, _error, id) => [
				{
					type: "Assessment",
					id,
				},
			],
		}),
		getLiveSessionActiveState: builder.query<LiveSessionActiveState, string>({
			queryFn: async (id, _api, _extra, fetchWithBQ) => {
				const result = await fetchWithBQ(`api/live/liveSession/${id}/liveState/userSession`);
				if (result.error?.status === 404) {
					return { data: { userSessions: [] } satisfies LiveSessionActiveState };
				}
				return result.data
					? ({
							...result,
							data: { userSessions: result.data as LiveUserSession[] } satisfies LiveSessionActiveState,
						} as any)
					: result;
			},
			providesTags: (_result, _error, id) => [
				{
					type: "LiveSessionActiveState",
					id,
				},
			],
		}),
		endLiveSession: builder.mutation<void, string>({
			query: (id) => ({
				url: `api/live/liveSession/${id}/end`,
				method: "POST",
			}),
			invalidatesTags: (_result, _error, id) => [
				{
					type: "LiveSession" as const,
					id,
				},
				"LiveSessions",
				"LiveSessionCount",
			],
		}),
	}),
});

export default liveSessionsApi;
